import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useInvestigationAccidentQuery } from "./hooks/useInvestigationAccidentQuery";
import { useInvestigationAccidentCommand } from "./hooks/useInvestigationAccidentCommand";
import { ROUTES } from "../../core/routes/routes";
import { DateUtil } from "../../common/utils/date.util";
import Table from "../../common/components/Table";
import Pagination from "../../common/components/Pagination";
import IconButton from "../../common/components/IconButton";
import Textfield from "../../common/components/Textfiled";
import { BsFileSpreadsheet, BsClipboardPlus, BsPencilSquare, BsTrash } from "react-icons/bs";

const columns = [
  {
    header: "Fecha de análisis",
    accessor: "committeeAnalysisDate",
    render: (val) => DateUtil.format(val),
  },
  {
    header: "Costo directo - Fatalidad",
    accessor: "directFatalityCosts",
    render: (val) => `$ ${parseFloat(val).toLocaleString("es-CO")}`,
  },
  {
    header: "Costo indirecto - Fatalidad",
    accessor: "indirectFatalityCosts",
    render: (val) => `$ ${parseFloat(val).toLocaleString("es-CO")}`,
  },
  {
    header: "Costo directo - Heridos grave",
    accessor: "directSevereInjuryCosts",
    render: (val) => `$ ${parseFloat(val).toLocaleString("es-CO")}`,
  },
  {
    header: "Costo indirecto - Heridos grave",
    accessor: "indirectSevereInjuryCosts",
    render: (val) => `$ ${parseFloat(val).toLocaleString("es-CO")}`,
  },
  {
    header: "Costo directo - Heridos leve",
    accessor: "directMinorInjuryCosts",
    render: (val) => `$ ${parseFloat(val).toLocaleString("es-CO")}`,
  },
  {
    header: "Costo indirecto - Heridos leve",
    accessor: "indirectMinorInjuryCosts",
    render: (val) => `$ ${parseFloat(val).toLocaleString("es-CO")}`,
  },
  {
    header: "Costo directo - Choque simple",
    accessor: "directSimpleCollisionCosts",
    render: (val) => `$ ${parseFloat(val).toLocaleString("es-CO")}`,
  },
  {
    header: "Costo indirecto - Choque simple",
    accessor: "indirectSimpleCollisionCosts",
    render: (val) => `$ ${parseFloat(val).toLocaleString("es-CO")}`,
  },
  {
    header: "Soporte de pago",
    accessor: "accidentPaymentProof",
    render: (val) => (val ? val : "No disponible"),
  },
  {
    header: "Agente causante",
    accessor: "injuryAgent",
  },
  {
    header: "Condición o comportamiento",
    accessor: "behaviorOrCondition",
  },
  {
    header: "Mecanismo del incidente",
    accessor: "incidentMechanism",
  },
  {
    header: "Días de incapacidad",
    accessor: "disabilityDays",
  },
  {
    header: "Tipo de lesión",
    accessor: "injuryType",
  },
  {
    header: "Parte del cuerpo afectada",
    accessor: "affectedBodyPart",
  },
  {
    header: "Acción correctiva",
    accessor: "correctiveAction",
  },
  {
    header: "Responsable de acción",
    accessor: "actionResponsible",
  },
  {
    header: "Fecha ejecución de acción",
    accessor: "actionExecutionDate",
    render: (val) => new Date(val).toLocaleDateString(),
  },
  {
    header: "Detalles del evento",
    accessor: "eventDetails",
    render: (val) =>
      val ? (
        <a href={val} target="_blank" rel="noopener noreferrer">
          Ver PDF
        </a>
      ) : (
        "No disponible"
      ),
  },
  {
    header: "Lección aprendida",
    accessor: "lessonLearned",
    render: (val) => val || "No disponible",
  },
  {
    header: "Evidencia divulgación",
    accessor: "lessonDisclosureEvidence",
    render: (val) => val || "No disponible",
  },
];

function ListInvestigations() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const investigationId = searchParams.get("investigationId");
  const [filters, setFilters] = useState({
    search: "",
    startDate: "",
    endDate: "",
    ...(investigationId ? { investigationId } : {}),
  });
  const {
    investigationAccidents,
    totalPages,
    loading,
    onChangePage,
    currentPage,
    refresh,
  } = useInvestigationAccidentQuery(filters);
  const { exportToExcel, deleteInvestigationAccident } = useInvestigationAccidentCommand(refresh);

  const columnsWithActions = [
    ...columns,
    {
      header: "Acciones",
      accessor: "actions",
      render: (_, row) => (
        <div className="d-flex align-items-center gap-2">
          <IconButton
            tooltipText="Editar"
            onClick={() =>
              navigate(ROUTES.REGISTER_INVESTIGATION, { state: { editData: row } })
            }
          >
            <BsPencilSquare size={20} />
          </IconButton>
          <IconButton
            tooltipText="Eliminar"
            className="text-danger"
            onClick={() => deleteInvestigationAccident(row.id)}
          >
            <BsTrash size={20} />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <main className="container-fluid p-4">
      <header className="d-flex justify-content-between align-items-center mb-4 px-3 py-2 border-bottom">
        <h1 className="h2 m-0 fw-bold">Lista de investigaciones</h1>
        <div className="d-flex gap-2">
          <IconButton
            tooltipText="Registrar"
            onClick={() => navigate(ROUTES.REGISTER_INVESTIGATION)}
          >
            <BsClipboardPlus size={24} />
          </IconButton>
          <IconButton
            tooltipText="Exportar"
            onClick={() => exportToExcel(filters)}
          >
            <BsFileSpreadsheet size={24} />
          </IconButton>
        </div>
      </header>

      <section className="shadow w-100 d-flex flex-column">
        <div className="d-flex gap-3 flex-wrap flex-md-nowrap">
          <div className="w-100">
            <Textfield
              label="Buscar"
              name="search"
              onChange={({ value }) => setFilters({ search: value })}
              placeholder="Buscar..."
              value={filters.search}
            />
          </div>

          <div className="d-flex gap-3 flex-wrap flex-md-nowrap justify-content-center">
            <Textfield
              label="Fecha inicio"
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={({ value }) =>
                setFilters((prev) => ({ ...prev, startDate: value }))
              }
            />
            <Textfield
              label="Fecha fin"
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={({ value }) =>
                setFilters((prev) => ({ ...prev, endDate: value }))
              }
            />
          </div>
        </div>

        <Table
          columns={columnsWithActions}
          data={investigationAccidents}
          showIndex={true}
          loading={loading}
        />

        <Pagination
          onChangePage={onChangePage}
          page={currentPage}
          totalPages={totalPages}
        />
      </section>
    </main>
  );
}

export default ListInvestigations;
